import React, { useState, useEffect } from 'react';
import { providersAPI } from '../../api/providers';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Textarea from '../../components/common/Textarea';
import { StarIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const ProviderReviews = () => {
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filter, setFilter] = useState('all'); // all, 5, 4, 3, 2, 1, responded, unresponded
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [responseError, setResponseError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const profile = await providersAPI.getMyProfile();
      setProvider(profile);
      const data = await providersAPI.getReviews(profile.id, { page, limit: 20 });
      setReviews(data.reviews || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (reviewId) => {
    if (!responseText.trim()) {
      setResponseError('Please write a response');
      return;
    }
    if (responseText.length > 500) {
      setResponseError('Response must be 500 characters or less');
      return;
    }

    setSubmitting(true);
    try {
      await providersAPI.respondToReview(reviewId, responseText.trim());
      toast.success('Response posted');
      setRespondingTo(null);
      setResponseText('');
      setResponseError('');
      fetchData(pagination.page);
    } catch (error) {
      console.error('Error responding:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter reviews
  const filteredReviews = reviews.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'responded') return !!r.providerResponse;
    if (filter === 'unresponded') return !r.providerResponse;
    return r.rating === parseInt(filter);
  });

  // Stats
  const ratingCounts = [0, 0, 0, 0, 0];
  reviews.forEach(r => { if (r.rating >= 1 && r.rating <= 5) ratingCounts[r.rating - 1]++; });
  const respondedCount = reviews.filter(r => r.providerResponse).length;

  const Stars = ({ rating, size = 16 }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        s <= rating
          ? <StarSolid key={s} style={{ width: size, height: size, color: '#facc15' }} />
          : <StarIcon key={s} style={{ width: size, height: size, color: 'var(--gray-6)' }} />
      ))}
    </div>
  );

  const timeAgo = (date) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Reviews & Ratings</h1>
          <p style={{ color: 'var(--gray-11)' }}>See what your clients are saying and respond to feedback</p>
        </div>

        <div className="flex gap-6" style={{ alignItems: 'flex-start' }}>
          {/* Left: Stats panel */}
          <div style={{ width: '280px', flexShrink: 0 }}>
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)', position: 'sticky', top: '80px' }}>
              {/* Overall rating */}
              <div className="text-center mb-4" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--gray-4)' }}>
                <p style={{ fontSize: '48px', fontWeight: 700, color: 'var(--gray-12)', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
                  {(provider?.rating || 0).toFixed(1)}
                </p>
                <Stars rating={Math.round(provider?.rating || 0)} size={20} />
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', marginTop: '4px' }}>
                  {pagination.total} review{pagination.total !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Rating breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                {[5, 4, 3, 2, 1].map(star => {
                  const count = ratingCounts[star - 1];
                  const pct = pagination.total > 0 ? (count / pagination.total) * 100 : 0;
                  return (
                    <button
                      key={star}
                      onClick={() => setFilter(filter === String(star) ? 'all' : String(star))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: filter === String(star) ? 'var(--orange-3)' : 'none',
                        border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: 'var(--gray-11)', width: '10px' }}>{star}</span>
                      <StarSolid style={{ width: '12px', height: '12px', color: '#facc15' }} />
                      <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: 'var(--gray-4)' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: '3px', backgroundColor: '#facc15' }} />
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--gray-10)', width: '20px', textAlign: 'right' }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Quick filters */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--gray-4)', paddingTop: '12px' }}>
                {[
                  { id: 'all', label: 'All Reviews' },
                  { id: 'unresponded', label: 'Needs Response' },
                  { id: 'responded', label: 'Responded' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    style={{
                      padding: '6px 10px', borderRadius: '6px', border: 'none',
                      fontSize: '13px', fontWeight: filter === f.id ? 600 : 400, cursor: 'pointer',
                      backgroundColor: filter === f.id ? 'var(--orange-9)' : 'transparent',
                      color: filter === f.id ? '#fff' : 'var(--gray-11)',
                      textAlign: 'left',
                    }}
                  >
                    {f.label}
                    {f.id === 'unresponded' && (
                      <span style={{
                        marginLeft: '6px', padding: '1px 6px', borderRadius: '10px', fontSize: '11px',
                        backgroundColor: filter === f.id ? 'rgba(255,255,255,0.3)' : 'var(--orange-3)',
                        color: filter === f.id ? '#fff' : 'var(--orange-9)',
                      }}>
                        {reviews.length - respondedCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: '1px', backgroundColor: 'var(--orange-6)', flexShrink: 0, alignSelf: 'stretch', minHeight: '200px' }} />

          {/* Right: Reviews list */}
          <div className="flex-1">
            {filteredReviews.length === 0 ? (
              <div className="text-center py-16">
                <ChatBubbleLeftRightIcon style={{ width: '48px', height: '48px', color: 'var(--gray-7)', margin: '0 auto 12px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '6px' }}>
                  {filter === 'all' ? 'No reviews yet' : 'No reviews match this filter'}
                </h3>
                <p style={{ color: 'var(--gray-10)', fontSize: '14px' }}>
                  {filter === 'all' ? 'Reviews will appear here once clients rate your services' : 'Try selecting a different filter'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredReviews.map(review => (
                  <div
                    key={review.id}
                    className="rounded-xl p-5"
                    style={{
                      backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)',
                      borderLeft: review.providerResponse ? '3px solid var(--green-9, #22c55e)' : '3px solid var(--orange-9)',
                    }}
                  >
                    {/* Review header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          backgroundColor: 'var(--orange-3)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)',
                        }}>
                          {review.userName?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                            {review.userName}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                            {timeAgo(review.createdAt)}
                          </p>
                        </div>
                      </div>
                      <Stars rating={review.rating} size={14} />
                    </div>

                    {/* Comment */}
                    {review.comment && (
                      <p style={{ fontSize: '14px', color: 'var(--gray-12)', lineHeight: 1.6, margin: '0 0 12px 0', textAlign: 'left' }}>
                        {review.comment}
                      </p>
                    )}

                    {/* Photos */}
                    {review.photos?.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {review.photos.map((url, i) => (
                          <img key={i} src={url} alt="" style={{ width: '60px', height: '60px', borderRadius: '6px', objectFit: 'cover' }} />
                        ))}
                      </div>
                    )}

                    {/* Provider response */}
                    {review.providerResponse && (
                      <div className="rounded-lg p-3 mt-2" style={{ backgroundColor: 'var(--gray-3)', borderLeft: '2px solid var(--orange-9)' }}>
                        <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', margin: '0 0 4px 0', textAlign: 'left' }}>
                          Your Response
                          {review.respondedAt && (
                            <span style={{ fontWeight: 400, color: 'var(--gray-10)', marginLeft: '8px' }}>
                              {timeAgo(review.respondedAt)}
                            </span>
                          )}
                        </p>
                        <p style={{ fontSize: '13px', color: 'var(--gray-12)', lineHeight: 1.5, margin: 0, textAlign: 'left' }}>
                          {review.providerResponse}
                        </p>
                      </div>
                    )}

                    {/* Respond button / form */}
                    {!review.providerResponse && (
                      respondingTo === review.id ? (
                        <div className="mt-3 space-y-3">
                          <Textarea
                            value={responseText}
                            onChange={(e) => {
                              setResponseText(e.target.value);
                              setResponseError('');
                            }}
                            placeholder="Write your response to this review..."
                            rows={3}
                            error={responseError}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: responseText.length > 500 ? 'var(--red-9)' : 'var(--gray-9)' }}>
                              {responseText.length}/500
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="small"
                                onClick={() => { setRespondingTo(null); setResponseText(''); setResponseError(''); }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                loading={submitting}
                                onClick={() => handleRespond(review.id)}
                              >
                                Post Response
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setRespondingTo(review.id); setResponseText(''); setResponseError(''); }}
                          style={{
                            marginTop: '8px', padding: '6px 14px', borderRadius: '6px',
                            border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                            color: 'var(--orange-9)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <ChatBubbleLeftRightIcon style={{ width: '14px', height: '14px' }} />
                          Respond
                        </button>
                      )
                    )}
                  </div>
                ))}

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => fetchData(page)}
                        style={{
                          width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                          fontSize: '13px', fontWeight: pagination.page === page ? 600 : 400,
                          cursor: 'pointer',
                          backgroundColor: pagination.page === page ? 'var(--orange-9)' : 'var(--gray-3)',
                          color: pagination.page === page ? '#fff' : 'var(--gray-11)',
                        }}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderReviews;
